/**
 * BookingFlows Connect — My Listings (operator side, admins only).
 * Your services are on the network by default; hide any (or everything) here.
 * Affiliates mark them up — your take never changes.
 * Going (back) on the network requires a one-time acceptance of the
 * BookingFlows Network Agreement (facilitation-only — we're not a party).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { connectApi } from '../../utils/apiV2';
import { ConnectShell, useConnectMe, money, CARD } from '../../components/connect/ConnectShell';
import ServiceToggle from '../../components/ServiceToggle';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { ListChecks, Loader2, ScrollText } from 'lucide-react';

export default function ConnectListings() {
  const navigate = useNavigate();
  const { me } = useConnectMe();
  const [listings, setListings] = useState(null);
  const [showAgreement, setShowAgreement] = useState(false);
  const [agreeing, setAgreeing] = useState(false);

  const load = useCallback(() => {
    connectApi.myListings().then((r) => setListings(r.data)).catch(() => setListings({ services: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (me && !me.isOrgAdmin) navigate('/connect', { replace: true });
  }, [me, navigate]);

  const saveVisibility = async (optOut, acceptAgreement = false) => {
    try {
      await connectApi.orgSettings(acceptAgreement ? { optOut, acceptAgreement: true } : { optOut });
      toast.success(optOut ? 'Your services are hidden from the network' : 'Your services are live on the network');
      load();
    } catch { toast.error('Could not save'); }
  };

  const handleVisibilityToggle = (visible) => {
    if (visible && !listings?.agreementAcceptedAt) {
      // Going live requires the one-time network agreement.
      setShowAgreement(true);
      return;
    }
    saveVisibility(!visible);
  };

  const acceptAndGoLive = async () => {
    setAgreeing(true);
    try {
      await saveVisibility(false, true);
      setShowAgreement(false);
    } finally {
      setAgreeing(false);
    }
  };

  const toggleService = async (svc, optIn) => {
    try {
      await connectApi.toggleListing(svc.id, optIn);
      setListings((l) => ({ ...l, services: l.services.map((s) => (s.id === svc.id ? { ...s, optedIn: optIn } : s)) }));
    } catch { toast.error('Could not save'); }
  };

  return (
    <ConnectShell>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10" data-testid="connect-listings-page">
        <div className="mb-6 cx-rise">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-2 flex items-center gap-1.5">
            <ListChecks className="w-3.5 h-3.5" /> Your company on the network
          </p>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">My Listings</h1>
          <p className="text-sm text-slate-500 mt-1.5 max-w-2xl leading-relaxed">
            Affiliates resell these at a markup — you get your full rate on every booking, and each one lands
            in your Manifest tagged <span className="font-bold text-emerald-700">Connect</span>.
            {listings?.inboundConnectBookings > 0 && ` ${listings.inboundConnectBookings} booking${listings.inboundConnectBookings !== 1 ? 's' : ''} so far.`}
          </p>
        </div>

        <div className={`${CARD} overflow-hidden cx-rise`} style={{ animationDelay: '80ms' }} data-testid="connect-listings-card">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
            <div className="flex-1">
              <p className="text-sm font-extrabold text-slate-900">Network visibility</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Listed by default — nothing to do unless you want out.</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-slate-500">{listings?.orgOptOut ? 'Hidden' : 'On the network'}</span>
              <span data-testid="connect-org-optout-switch">
                <ServiceToggle checked={!listings?.orgOptOut} onChange={handleVisibilityToggle} />
              </span>
            </div>
          </div>
          {!listings && <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-emerald-300" /></div>}
          <div className={`divide-y divide-slate-50 ${listings?.orgOptOut ? 'opacity-40 pointer-events-none' : ''}`}>
            {(listings?.services || []).map((s) => (
              <div key={s.id} className="px-5 py-3 flex items-center gap-3" data-testid={`connect-listing-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-extrabold text-slate-800 truncate">{s.name}</p>
                  <p className="text-[11px] text-slate-400">{money(s.price, s.currency)}{s.category ? ` · ${s.category}` : ''}</p>
                </div>
                <span data-testid={`connect-listing-switch-${s.id}`}>
                  <ServiceToggle checked={s.optedIn} onChange={(v) => toggleService(s, v)} />
                </span>
              </div>
            ))}
          </div>
        </div>

        {listings?.agreementAcceptedAt && (
          <p className="mt-3 text-[11px] text-slate-400 flex items-center gap-1.5" data-testid="connect-agreement-accepted-note">
            <ScrollText className="w-3.5 h-3.5" />
            Network Agreement accepted {new Date(listings.agreementAcceptedAt).toLocaleDateString()}
          </p>
        )}

        {/* One-time Network Agreement — required before going live on the network */}
        <Dialog open={showAgreement} onOpenChange={setShowAgreement}>
          <DialogContent className="max-w-lg" data-testid="connect-agreement-modal">
            <DialogHeader>
              <DialogTitle>BookingFlows Network Agreement</DialogTitle>
              <DialogDescription>
                A quick one-time acceptance before your services go live on the network.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-lg bg-slate-50 p-4 max-h-56 overflow-y-auto text-xs text-slate-600 leading-relaxed space-y-2.5">
              <p><span className="font-semibold text-slate-800">1. Facilitation only.</span> BookingFlows operates the Connect network as a technology facilitator. We are not a party to, and assume no liability for, any transaction, service, or arrangement between operators, affiliates, and customers.</p>
              <p><span className="font-semibold text-slate-800">2. Your services, your responsibility.</span> You remain solely responsible for the accuracy of your listings and the delivery, safety, legality, licensing, and insurance of every service you fulfil — including bookings that arrive through affiliates.</p>
              <p><span className="font-semibold text-slate-800">3. Pricing & payouts.</span> Affiliates may resell your services at a markup; you always receive your full listed rate. Payouts follow your standard BookingFlows payment terms.</p>
              <p><span className="font-semibold text-slate-800">4. Disputes.</span> Disputes arising from a booking are between the transacting parties. You agree to hold BookingFlows harmless from claims arising out of your services or listings.</p>
              <p><span className="font-semibold text-slate-800">5. Removal & changes.</span> You can hide your listings at any time. BookingFlows may remove listings that violate our <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">Terms of Service</a> and may update these network terms with notice.</p>
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <Button variant="outline" onClick={() => setShowAgreement(false)} data-testid="connect-agreement-cancel">
                Cancel
              </Button>
              <Button
                onClick={acceptAndGoLive}
                disabled={agreeing}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="connect-agreement-accept"
              >
                {agreeing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                I agree — put me on the network
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </ConnectShell>
  );
}
